import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import RecordCellEditor from './RecordCellEditor';
import { getApiUrl } from '../utils/apiConfig';
import {
  buildFallbackDetailFields,
  fieldLabelFor,
  formatRecordFieldValue,
  HIDDEN_DETAIL_FIELDS,
  resolveRecordTitle,
} from '../utils/recordFieldDisplay';

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a ?? '') === String(b ?? '');
}

function isFieldEditable(fieldName, fieldMetaByName, objectApiName) {
  if (!fieldName || fieldName === 'id') return false;
  if (HIDDEN_DETAIL_FIELDS.has(fieldName)) return false;
  if (objectApiName === 'user_breaks' && fieldName === 'video_link') return true;
  return fieldMetaByName[fieldName]?.is_editable === true;
}

export default function RecordRelatedBulkEditModal({
  visible,
  items = [],
  currentUser,
  onClose,
  onComplete,
  theme,
  isUSUser,
}) {
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState(null);
  const [fieldMeta, setFieldMeta] = useState([]);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [error, setError] = useState(null);

  const item = items[index] || null;
  const styles = useMemo(() => getStyles(theme), [theme]);

  const fieldMetaByName = useMemo(() => {
    const map = {};
    for (const field of fieldMeta) {
      if (field?.field_name) map[field.field_name] = field;
    }
    if (item?.object_api_name === 'user_breaks' && map.video_link) {
      map.video_link = { ...map.video_link, is_editable: true, input_type: 'json_array' };
    }
    return map;
  }, [fieldMeta, item?.object_api_name]);

  const hasDraftChanges = useMemo(() => {
    if (!baseline || !draft) return false;
    for (const [fieldName, meta] of Object.entries(fieldMetaByName)) {
      if (!meta?.is_editable) continue;
      if (!valuesEqual(baseline[fieldName], draft[fieldName])) return true;
    }
    return false;
  }, [baseline, draft, fieldMetaByName]);

  const resetRecordState = useCallback(() => {
    setRecord(null);
    setFieldMeta([]);
    setDraft(null);
    setBaseline(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!visible) {
      setIndex(0);
      resetRecordState();
      return;
    }
    setIndex(0);
  }, [visible, items, resetRecordState]);

  useEffect(() => {
    if (!visible || !item?.object_api_name || item?.id == null) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      resetRecordState();
      try {
        const [recordRes, fieldsRes] = await Promise.all([
          fetch(
            `${getApiUrl()}/api/objects/${encodeURIComponent(item.object_api_name)}/records/${encodeURIComponent(String(item.id))}`
          ),
          fetch(
            `${getApiUrl()}/api/objects/${encodeURIComponent(item.object_api_name)}/fields`
          ),
        ]);
        const recordBody = await recordRes.json().catch(() => ({}));
        const fieldsBody = fieldsRes.ok ? await fieldsRes.json() : [];
        if (!recordRes.ok) {
          throw new Error(recordBody.error || 'Failed to load record');
        }
        if (cancelled) return;
        const row = recordBody.record || null;
        setRecord(row);
        setFieldMeta(Array.isArray(fieldsBody) ? fieldsBody : []);
        setBaseline(row ? { ...row } : null);
        setDraft(row ? { ...row } : null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load record');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, item?.object_api_name, item?.id, resetRecordState]);

  const advanceOrFinish = useCallback(() => {
    if (index < items.length - 1) {
      setIndex((prev) => prev + 1);
    } else {
      onComplete?.();
    }
  }, [index, items.length, onComplete]);

  const handleSave = async () => {
    if (!item || !currentUser?.id || saving) return;

    if (!hasDraftChanges) {
      advanceOrFinish();
      return;
    }

    const fields = {};
    for (const [fieldName, meta] of Object.entries(fieldMetaByName)) {
      if (!meta?.is_editable) continue;
      if (!valuesEqual(baseline[fieldName], draft[fieldName])) {
        fields[fieldName] = draft[fieldName];
      }
    }
    if (Object.keys(fields).length === 0) {
      advanceOrFinish();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(item.object_api_name)}/records`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            updates: [{ id: parseInt(String(item.id), 10), fields }],
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      advanceOrFinish();
    } catch (err) {
      setError(err.message || 'Could not save record');
    } finally {
      setSaving(false);
    }
  };

  const title = draft ? resolveRecordTitle(draft, fieldMetaByName) : item?.title || 'Record';
  const fields = buildFallbackDetailFields(draft || record, fieldMetaByName);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View
          style={[styles.card, { backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff' }]}
          {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkModal: 'true' } } : {})}
        >
          <View style={styles.header}>
            <View style={styles.headerTextWrap}>
              <Text style={[styles.headerEyebrow, { color: theme.colors.text }]}>
                {item?.object_label || item?.object_api_name || 'Record'} · {index + 1} of{' '}
                {items.length}
              </Text>
              <Text style={[styles.headerTitle, { color: theme.colors.heading }]} numberOfLines={2}>
                {title}
              </Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} accessibilityLabel="Close">
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.centerState}>
              <ActivityIndicator size="large" color="#d4af37" />
            </View>
          ) : (
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              {error ? <Text style={styles.errorText}>{error}</Text> : null}
              {fields.map(({ fieldName, label }) => {
                const meta = fieldMetaByName[fieldName];
                const editable = isFieldEditable(fieldName, fieldMetaByName, item?.object_api_name);
                const value = draft?.[fieldName];
                return (
                  <View key={fieldName} style={styles.fieldRow}>
                    <Text style={[styles.fieldLabel, { color: theme.colors.text }]}>{label}</Text>
                    {editable ? (
                      <RecordCellEditor
                        value={value}
                        fieldMeta={meta}
                        theme={theme}
                        isUSUser={isUSUser}
                        onChange={(next) =>
                          setDraft((prev) => (prev ? { ...prev, [fieldName]: next } : prev))
                        }
                      />
                    ) : (
                      <Text style={[styles.fieldValue, { color: theme.colors.text }]}>
                        {formatRecordFieldValue(value, meta, isUSUser)}
                      </Text>
                    )}
                  </View>
                );
              })}
            </ScrollView>
          )}

          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.footerBtn, styles.cancelBtn, { borderColor: theme.colors.border }]}
              onPress={onClose}
              disabled={saving}
              {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkCancel: 'true' } } : {})}
            >
              <Text style={[styles.cancelBtnText, { color: theme.colors.text }]}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.footerBtn, styles.saveBtn, saving && styles.saveBtnDisabled]}
              onPress={() => void handleSave()}
              disabled={saving || loading}
              {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkSave: 'true' } } : {})}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#111" />
              ) : (
                <Text style={styles.saveBtnText}>
                  {index < items.length - 1 ? 'Save & next' : 'Save & close'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'center',
      padding: 20,
    },
    card: {
      maxWidth: 720,
      width: '100%',
      alignSelf: 'center',
      maxHeight: '85%',
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    headerTextWrap: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    headerEyebrow: {
      fontSize: 12,
      opacity: 0.85,
      backgroundColor: 'transparent',
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: '600',
      marginTop: 4,
      backgroundColor: 'transparent',
    },
    closeBtn: {
      padding: 4,
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    closeBtnText: {
      fontSize: 18,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    centerState: {
      paddingVertical: 48,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    body: {
      flexGrow: 0,
      backgroundColor: 'transparent',
    },
    bodyContent: {
      padding: 16,
      paddingBottom: 24,
    },
    fieldRow: {
      marginBottom: 14,
      backgroundColor: 'transparent',
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    fieldValue: {
      fontSize: 14,
      backgroundColor: 'transparent',
    },
    errorText: {
      color: '#ba0517',
      marginBottom: 12,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 10,
      padding: 14,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    footerBtn: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 6,
      minWidth: 110,
      alignItems: 'center',
    },
    cancelBtn: {
      borderWidth: 1,
      backgroundColor: 'transparent',
    },
    cancelBtnText: {
      fontSize: 14,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    saveBtn: {
      backgroundColor: '#d4af37',
    },
    saveBtnDisabled: {
      opacity: 0.6,
    },
    saveBtnText: {
      fontSize: 14,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
  });
}
